import { motion, useReducedMotion } from 'framer-motion';
import { Home, MapPin, Navigation, ShieldCheck, Star } from 'lucide-react';

const pins = [
  { city: 'Kathmandu', price: 'NPR 28k', x: '18%', y: '58%', delay: 0 },
  { city: 'Dubai', price: '$1.8k', x: '68%', y: '34%', delay: 0.35 },
  { city: 'London', price: '£920', x: '49%', y: '49%', delay: 0.7 },
  { city: 'Tokyo', price: '¥150k', x: '78%', y: '64%', delay: 1.05 },
];

const cards = [
  {
    title: 'Skyline Studio',
    place: 'Dubai Marina',
    meta: 'Verified owner',
    x: '62%',
    y: '14%',
    delay: 0.25,
  },
  {
    title: 'Garden Flat',
    place: 'London Zone 2',
    meta: 'Exact pin live',
    x: '11%',
    y: '24%',
    delay: 0.55,
  },
  {
    title: 'Rooftop Room',
    place: 'Lazimpat',
    meta: 'Direct message',
    x: '57%',
    y: '68%',
    delay: 0.85,
  },
];

const buildingColumns = [
  'h-[34%]',
  'h-[48%]',
  'h-[42%]',
  'h-[62%]',
  'h-[38%]',
  'h-[54%]',
  'h-[45%]',
  'h-[70%]',
  'h-[50%]',
  'h-[58%]',
];

const globePins = [
  { x: '28%', y: '38%', delay: 0.1 },
  { x: '54%', y: '31%', delay: 0.45 },
  { x: '68%', y: '57%', delay: 0.8 },
];

export default function LivingRoomHoraScene({ variant = 'hero', className = '' }) {
  const prefersReducedMotion = useReducedMotion();
  const isHero = variant === 'hero';
  const isAuth = variant === 'auth';
  const isPanel = variant === 'panel';
  const pinScale = isAuth ? 0.88 : 1;
  const routeGradientId = `route-glow-${variant}`;
  const scenePins = isHero
    ? [
        { city: 'Dubai', price: '$1.8k', x: '70%', y: '36%', delay: 0.2 },
        { city: 'London', price: '£920', x: '59%', y: '52%', delay: 0.55 },
        { city: 'Tokyo', price: '¥150k', x: '82%', y: '64%', delay: 0.9 },
      ]
    : pins;
  const globeSizeClass = isAuth
    ? 'right-[-6rem] top-12 h-72 w-72 opacity-70 md:right-[-3rem] md:h-80 md:w-80'
    : isPanel
      ? 'right-[-3rem] top-8 h-64 w-64 opacity-65 md:h-80 md:w-80'
      : 'right-[-12%] top-[8%] h-[32rem] w-[32rem] opacity-95 md:right-[-8%] lg:right-[1%] lg:h-[42rem] lg:w-[42rem] xl:right-[4%] xl:h-[48rem] xl:w-[48rem]';

  return (
    <div
      className={`relative h-full min-h-[420px] w-full overflow-hidden bg-[#07111f] ${className}`}
      aria-hidden="true"
    >
      <motion.div
        className="absolute inset-0"
        style={{
          backgroundImage:
            isHero
              ? 'radial-gradient(circle at 78% 38%, rgba(56,189,248,0.22), transparent 28%), radial-gradient(circle at 12% 18%, rgba(244,63,94,0.14), transparent 24%), linear-gradient(135deg, #020617 0%, #07111f 28%, #10233d 55%, #050816 100%)'
              : 'linear-gradient(135deg, #07111f 0%, #10233d 30%, #134e5e 56%, #25345d 78%, #090d18 100%)',
          backgroundSize: '180% 180%',
        }}
        animate={
          prefersReducedMotion
            ? undefined
            : { backgroundPosition: ['0% 40%', '88% 56%', '16% 88%', '0% 40%'] }
        }
        transition={{ duration: 26, repeat: Infinity, ease: 'easeInOut' }}
      />

      {isHero && (
        <motion.div
          className="absolute inset-0 opacity-55"
          style={{
            backgroundImage:
              'radial-gradient(circle at 12% 18%, rgba(255,255,255,0.75) 0 1px, transparent 1.5px), radial-gradient(circle at 56% 38%, rgba(186,230,253,0.65) 0 1px, transparent 1.6px), radial-gradient(circle at 78% 72%, rgba(255,255,255,0.55) 0 1px, transparent 1.4px)',
            backgroundSize: '180px 180px, 260px 260px, 340px 340px',
          }}
          animate={prefersReducedMotion ? undefined : { x: [0, -28, 0], y: [0, 18, 0] }}
          transition={{ duration: 28, repeat: Infinity, ease: 'linear' }}
        />
      )}

      <motion.div
        className="absolute inset-x-[-10%] top-[8%] h-[58%] opacity-30"
        style={{
          backgroundImage:
            'linear-gradient(rgba(125,211,252,0.28) 1px, transparent 1px), linear-gradient(90deg, rgba(125,211,252,0.22) 1px, transparent 1px)',
          backgroundSize: '72px 72px',
          transform: 'perspective(720px) rotateX(62deg)',
          transformOrigin: 'center top',
        }}
        animate={prefersReducedMotion ? undefined : { y: [0, -36, 0] }}
        transition={{ duration: 18, repeat: Infinity, ease: 'linear' }}
      />

      <svg
        className="absolute inset-0 h-full w-full opacity-90"
        viewBox="0 0 1000 700"
        preserveAspectRatio="none"
      >
        <defs>
          <linearGradient id={routeGradientId} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#38bdf8" stopOpacity="0.15" />
            <stop offset="45%" stopColor="#f59e0b" stopOpacity="0.8" />
            <stop offset="100%" stopColor="#fb7185" stopOpacity="0.75" />
          </linearGradient>
        </defs>
        <motion.path
          d="M70 470 C190 380 270 545 395 430 C525 310 585 398 700 270 C805 150 900 260 952 190"
          fill="none"
          stroke={`url(#${routeGradientId})`}
          strokeWidth="5"
          strokeLinecap="round"
          strokeDasharray="18 22"
          initial={{ pathLength: 0.25, opacity: 0.5 }}
          animate={
            prefersReducedMotion
              ? { pathLength: 1, opacity: 0.75 }
              : { pathLength: [0.35, 1, 0.35], opacity: [0.45, 0.92, 0.45] }
          }
          transition={{ duration: 9, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.path
          d="M160 170 C280 244 330 140 455 220 C575 298 628 198 756 315 C835 386 884 352 955 425"
          fill="none"
          stroke="#7dd3fc"
          strokeWidth="2"
          strokeLinecap="round"
          strokeDasharray="8 18"
          opacity="0.45"
          animate={prefersReducedMotion ? undefined : { strokeDashoffset: [0, -140] }}
          transition={{ duration: 12, repeat: Infinity, ease: 'linear' }}
        />
      </svg>

      <motion.div
        className={`absolute z-[12] hidden rounded-full ${isHero ? 'md:block' : 'sm:block'} ${globeSizeClass}`}
        initial={{ opacity: 0, scale: 0.92 }}
        animate={
          prefersReducedMotion
            ? { opacity: isAuth || isPanel ? 0.68 : 0.9, scale: 1 }
            : { opacity: isAuth || isPanel ? 0.68 : 0.92, scale: [0.98, 1.015, 0.98] }
        }
        transition={{ duration: isHero ? 12 : 9, repeat: Infinity, ease: 'easeInOut' }}
      >
        <motion.div
          className="absolute inset-0 overflow-hidden rounded-full border border-sky-100/45 bg-[radial-gradient(circle_at_32%_23%,rgba(255,255,255,0.92),rgba(147,197,253,0.74)_16%,rgba(59,130,246,0.72)_40%,rgba(30,64,175,0.58)_65%,rgba(15,23,42,0.30)_100%)] shadow-[0_0_150px_rgba(56,189,248,0.46),inset_18px_-28px_80px_rgba(2,6,23,0.58),inset_-18px_18px_48px_rgba(255,255,255,0.18)]"
          animate={prefersReducedMotion ? undefined : { rotate: [0, 360] }}
          transition={{ duration: isHero ? 70 : 46, repeat: Infinity, ease: 'linear' }}
        >
          <span className="absolute inset-[10%] rounded-full border border-white/15" />
          <span className="absolute inset-x-[9%] top-[31%] h-px bg-white/15" />
          <span className="absolute inset-x-[7%] top-1/2 h-px bg-white/20" />
          <span className="absolute inset-x-[9%] top-[69%] h-px bg-white/15" />
          <span className="absolute inset-y-[7%] left-1/2 w-px bg-white/15" />
          <span className="absolute inset-y-[10%] left-[25%] w-[23%] rounded-full border-l border-white/15" />
          <span className="absolute inset-y-[10%] right-[25%] w-[23%] rounded-full border-r border-white/15" />
          <svg className="absolute inset-0 h-full w-full" viewBox="0 0 220 220" aria-hidden="true">
            <path
              d="M49 62 C66 45 95 43 120 56 C112 72 88 76 72 89 C61 98 48 84 49 62Z"
              fill="rgba(34,197,94,0.34)"
            />
            <path
              d="M126 76 C153 70 181 90 188 120 C164 122 143 108 128 91 C123 85 121 79 126 76Z"
              fill="rgba(22,163,74,0.30)"
            />
            <path
              d="M86 128 C113 118 143 136 154 164 C134 177 101 166 80 146 C74 139 78 132 86 128Z"
              fill="rgba(132,204,22,0.23)"
            />
            <path
              d="M42 116 C60 105 87 108 103 119 C91 133 68 137 48 128 C39 124 36 120 42 116Z"
              fill="rgba(250,204,21,0.20)"
            />
            <path
              d="M28 82 C72 63 108 68 150 84 C176 94 194 91 207 86"
              fill="none"
              stroke="rgba(255,255,255,0.58)"
              strokeWidth="6"
              strokeLinecap="round"
              opacity="0.55"
            />
            <path
              d="M31 142 C72 128 111 134 145 149 C165 158 184 158 203 153"
              fill="none"
              stroke="rgba(255,255,255,0.48)"
              strokeWidth="8"
              strokeLinecap="round"
              opacity="0.5"
            />
            <motion.path
              d="M42 150 C84 90 138 82 190 108"
              fill="none"
              stroke="rgba(253,224,71,0.78)"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeDasharray="5 9"
              animate={prefersReducedMotion ? undefined : { strokeDashoffset: [0, -112] }}
              transition={{ duration: 7, repeat: Infinity, ease: 'linear' }}
            />
            <motion.path
              d="M58 74 C97 126 142 142 178 168"
              fill="none"
              stroke="rgba(125,211,252,0.55)"
              strokeWidth="2"
              strokeLinecap="round"
              strokeDasharray="4 10"
              animate={prefersReducedMotion ? undefined : { strokeDashoffset: [0, -96] }}
              transition={{ duration: 8.5, repeat: Infinity, ease: 'linear' }}
            />
          </svg>
          <span className="absolute inset-0 rounded-full bg-[radial-gradient(circle_at_72%_45%,transparent_0%,transparent_48%,rgba(2,6,23,0.28)_70%,rgba(2,6,23,0.68)_100%)]" />
          {globePins.map((pin, index) => (
            <motion.span
              key={index}
              className="absolute h-2.5 w-2.5 rounded-full border border-white/70 bg-rose-400 shadow-[0_0_18px_rgba(251,113,133,0.72)]"
              style={{ left: pin.x, top: pin.y }}
              animate={prefersReducedMotion ? undefined : { scale: [1, 1.8, 1], opacity: [0.95, 0.45, 0.95] }}
              transition={{ duration: 2.8, delay: pin.delay, repeat: Infinity, ease: 'easeInOut' }}
            />
          ))}
        </motion.div>
        <span className="absolute -inset-6 rounded-full border border-sky-300/10" />
        {isHero && (
          <>
            <span className="absolute -inset-10 rounded-full border border-sky-200/10" />
            <motion.span
              className="absolute left-[-8%] top-[45%] h-1 w-[116%] rounded-full bg-gradient-to-r from-transparent via-amber-200/55 to-transparent shadow-[0_0_30px_rgba(252,211,77,0.35)]"
              animate={prefersReducedMotion ? undefined : { rotate: [8, 14, 8], opacity: [0.35, 0.72, 0.35] }}
              transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
            />
          </>
        )}
      </motion.div>

      <div className="absolute inset-x-0 bottom-0 h-[45%]">
        <div className="absolute inset-x-0 bottom-0 h-full bg-gradient-to-t from-[#060914] via-[#07111f]/85 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 flex h-full items-end justify-between gap-2 px-5 opacity-55">
          {buildingColumns.map((height, index) => (
            <motion.div
              key={index}
              className={`${height} flex-1 rounded-t-md bg-gradient-to-t from-slate-950 via-slate-800 to-sky-900/80 shadow-[inset_0_1px_0_rgba(255,255,255,0.12)]`}
              animate={prefersReducedMotion ? undefined : { opacity: [0.45, 0.78, 0.45] }}
              transition={{
                duration: 5 + index * 0.35,
                delay: index * 0.18,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
            >
              <div className="grid h-full grid-cols-2 gap-1 p-2 opacity-70">
                {Array.from({ length: 8 }).map((_, lightIndex) => (
                  <span
                    key={lightIndex}
                    className="h-1.5 rounded-full bg-amber-200/45"
                    style={{ opacity: (lightIndex + index) % 3 === 0 ? 0.9 : 0.25 }}
                  />
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {scenePins.map((pin) => (
        <motion.div
          key={pin.city}
          className="absolute z-20"
          style={{ left: pin.x, top: pin.y, transform: `scale(${pinScale})` }}
          initial={{ opacity: 0, y: 16 }}
          animate={
            prefersReducedMotion
              ? { opacity: 1, y: 0 }
              : { opacity: 1, y: [0, -8, 0] }
          }
          transition={{
            opacity: { duration: 0.5, delay: pin.delay },
            y: { duration: 4.8, delay: pin.delay, repeat: Infinity, ease: 'easeInOut' },
          }}
        >
          <span className="absolute left-1/2 top-1/2 h-9 w-9 -translate-x-1/2 -translate-y-1/2 rounded-full border border-sky-300/45 bg-sky-300/10 md:h-12 md:w-12" />
          <span className="relative flex h-8 w-8 items-center justify-center rounded-full border border-white/25 bg-white/85 text-rose-500 shadow-[0_14px_40px_rgba(2,6,23,0.28)] backdrop-blur md:hidden">
            <MapPin className="h-4 w-4" />
          </span>
          <span className="relative hidden items-center gap-2 rounded-full border border-white/25 bg-white/90 px-3 py-2 text-xs font-bold text-slate-900 shadow-[0_14px_40px_rgba(2,6,23,0.28)] backdrop-blur md:flex">
            <MapPin className="h-4 w-4 text-rose-500" />
            {pin.price}
          </span>
        </motion.div>
      ))}

      {!isAuth && !isHero &&
        cards.map((card) => (
          <motion.div
            key={card.title}
            className="absolute z-30 hidden w-56 rounded-2xl border border-white/20 bg-white/10 p-4 text-white shadow-[0_24px_60px_rgba(0,0,0,0.28)] backdrop-blur-xl md:block"
            style={{ left: card.x, top: card.y }}
            initial={{ opacity: 0, y: 18, scale: 0.96 }}
            animate={
              prefersReducedMotion
                ? { opacity: 1, y: 0, scale: 1 }
                : { opacity: 1, y: [0, -10, 0], scale: 1 }
            }
            transition={{
              opacity: { duration: 0.6, delay: card.delay },
              y: { duration: 7, delay: card.delay, repeat: Infinity, ease: 'easeInOut' },
              scale: { duration: 0.6, delay: card.delay },
            }}
          >
            <div className="mb-3 flex items-center justify-between">
              <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-white/20">
                <Home className="h-4 w-4 text-sky-200" />
              </span>
              <span className="flex items-center gap-1 rounded-full bg-emerald-400/15 px-2 py-1 text-[11px] font-semibold text-emerald-100">
                <ShieldCheck className="h-3 w-3" />
                Live
              </span>
            </div>
            <p className="text-sm font-bold">{card.title}</p>
            <p className="mt-1 text-xs text-white/70">{card.place}</p>
            <div className="mt-3 flex items-center justify-between text-xs text-white/70">
              <span>{card.meta}</span>
              <span className="flex items-center gap-1 text-amber-200">
                <Star className="h-3 w-3 fill-current" />
                4.9
              </span>
            </div>
          </motion.div>
        ))}

      <motion.div
        className="absolute inset-y-0 z-10 w-1/3 bg-gradient-to-r from-transparent via-white/12 to-transparent"
        initial={{ x: '-45%' }}
        animate={prefersReducedMotion ? undefined : { x: ['-45%', '245%'] }}
        transition={{ duration: 8.5, repeat: Infinity, ease: 'easeInOut' }}
      />

      <motion.div
        className="absolute bottom-8 right-8 z-30 hidden items-center gap-3 rounded-full border border-white/16 bg-white/12 px-4 py-3 text-white shadow-[0_20px_50px_rgba(0,0,0,0.26)] backdrop-blur-xl sm:flex"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.55, delay: 0.6 }}
      >
        <span className="flex h-10 w-10 items-center justify-center rounded-full bg-sky-400/20 text-sky-100">
          <Navigation className="h-5 w-5" />
        </span>
        <span>
          <span className="block text-sm font-bold">{isPanel ? 'Owner view' : 'Map view is alive'}</span>
          <span className="block text-xs text-white/65">Pins, prices, and owners in motion</span>
        </span>
      </motion.div>

      <div className="absolute inset-0 bg-gradient-to-r from-slate-950/82 via-slate-950/36 to-slate-950/8" />
      <div className="absolute inset-0 bg-gradient-to-b from-slate-950/20 via-transparent to-slate-950/80" />
    </div>
  );
}
